import React, { useRef } from 'react';
import CrudListPage from './CrudListPage';
import SectionCard from '../ui/SectionCard';
import { Button } from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import { PlusIcon, BuildingOffice2Icon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

/**
 * CompetitorsPage — Settings › Competitors & Lost Reasons (Iter18 §2.12).
 *
 * Combined page with two lists so both concepts sit together (they're used
 * together on the quote loss-reason flow):
 *   • Competitors  — for attributing lost quotes to a rival.
 *   • Lost Reasons — for categorizing why a quote was lost.
 *
 * SUPERSESSION NOTE (2026-07-31):
 *   Iter18 Polish Round 2 rev 5 (2026-07-17) scoped `SectionCard` to Customer
 *   Types only, and this page previously used bare `<CrudListPage>` without a
 *   wrapping surface. That was a Round-2 pacing decision (operator: "style
 *   update is for customer type only") — not a structural boundary.
 *
 *   Superseded 2026-07-31 (§3.1 SectionHeader wave, DEC-48 "build components
 *   not pages"): the SectionCard pattern now applies to all six settings
 *   surfaces that carry section headers. This page mounts <SectionCard> ×2 to
 *   match the Customer Types reference. Reason: scope-expansion-by-operator,
 *   not error correction — rev-5 was correct when it was made.
 *
 *   See iter18-settings-polish-round-2.md §0.4 / §1.9 supersession footnote.
 */
export default function CompetitorsPage() {
  const competitorsRef = useRef(null);
  const lostReasonsRef = useRef(null);

  const addButton = (ref, label) => (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => ref.current?.openAdd()}
    >
      <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" />
      {label}
    </Button>
  );

  return (
    <div>
      <PageHeader
        title="Competitors & Lost Reasons"
        description="Rival contractors and the reasons quotes are lost. Used together on the quote loss-reason flow to drive win-rate analytics."
      />

      <div className="space-y-4">
        <SectionCard
          title="Competitors"
          description="Rival contractors you compete against. Selected on lost quotes for win-rate analysis."
          icon={BuildingOffice2Icon}
          actions={addButton(competitorsRef, 'Add')}
        >
          <CrudListPage
            ref={competitorsRef}
            bare
            listType="competitors"
            fields={[
              { key: 'name', label: 'Competitor Name', type: 'text', required: true, placeholder: 'e.g., ABC Insulation' },
              { key: 'website', label: 'Website', type: 'url', required: false, placeholder: 'https://…' },
              { key: 'notes', label: 'Notes', type: 'text', required: false, placeholder: 'Optional notes' },
            ]}
            emptyMessage="No competitors tracked. Add competitors so lost quotes can be attributed to them."
          />
        </SectionCard>

        <SectionCard
          title="Lost Reasons"
          description="Why quotes are lost. Drives lost-reason analytics on the quote loss dialog."
          icon={ArrowTrendingDownIcon}
          actions={addButton(lostReasonsRef, 'Add')}
        >
          <CrudListPage
            ref={lostReasonsRef}
            bare
            listType="lost_reasons"
            fields={[
              { key: 'name', label: 'Reason', type: 'text', required: true, placeholder: 'e.g., Price too high' },
              { key: 'notes', label: 'Description', type: 'text', required: false, placeholder: 'Optional detail' },
            ]}
            emptyMessage="No lost reasons configured. Common examples: Price too high, Timing, Chose competitor, No response."
          />
        </SectionCard>
      </div>
    </div>
  );
}
